import { Brand } from '../brand';
import { TreatmentTemplate } from '../clinical-charting/treatment/treatment-template';
import {
  IBrand,
  IMultiTreatmentConfiguration,
  IPractice,
  ITreatmentConfiguration,
  isMultiTreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  WithRef,
  undeletedFilter,
} from '@principle-theorem/shared';
import {
  classifyKnowledgeBaseTreatment,
  TreatmentClassification,
} from './knowledge-base-treatment-classifier';

export interface IKnowledgeBaseDerivedItem {
  templateId: string;
  name: string;
  description?: string;
  priceRange: string;
  classification: TreatmentClassification;
  isBookable: boolean;
}

export interface IKnowledgeBaseTemplateDerivation {
  items: IKnowledgeBaseDerivedItem[];
  generalServices: string;
  cosmeticServices: string;
}

export async function deriveKnowledgeBaseFromTemplates(args: {
  practice: WithRef<IPractice>;
  brandRef: DocumentReference<IBrand>;
  excludedTemplateIds?: Set<string>;
}): Promise<IKnowledgeBaseTemplateDerivation> {
  const { practice, brandRef, excludedTemplateIds = new Set<string>() } = args;

  const templates = await Brand.treatmentTemplates({ ref: brandRef });

  const categoryDocs = await Firestore.getDocs(
    Brand.treatmentCategoryCol({ ref: brandRef })
  );

  const categoryMap = new Map(
    categoryDocs.map((category) => [category.ref.id, category.name])
  );

  const enabledTemplates = templates.filter((template) =>
    TreatmentTemplate.practiceIsEnabled(template, practice)
  );

  const nonExcludedTemplates = enabledTemplates.filter(
    (template) => !excludedTemplateIds.has(template.ref.id)
  );

  const items: IKnowledgeBaseDerivedItem[] = [];

  for (const template of nonExcludedTemplates) {
    const treatmentRef = TreatmentTemplate.getTreatment(template);

    if (!treatmentRef) {
      continue;
    }

    const config = await Firestore.safeGetDoc(treatmentRef.ref);

    if (!config || !undeletedFilter(config)) {
      continue;
    }

    const isMulti = isMultiTreatmentConfiguration(config);

    const description = template.description || undefined;

    let categoryName: string | undefined;
    let keyword: string | undefined;

    if (isMulti) {
      keyword = (config as WithRef<IMultiTreatmentConfiguration>).keyword;
    } else {
      const categoryRef = (config as WithRef<ITreatmentConfiguration>).category;
      if (categoryRef) {
        categoryName = categoryMap.get(categoryRef.id);
      }
    }

    const classification = classifyKnowledgeBaseTreatment({
      templateName: template.name,
      categoryName,
      keyword,
    });

    items.push({
      templateId: template.ref.id,
      name: template.name,
      description,
      priceRange: '',
      classification,
      isBookable: TreatmentTemplate.canBeBookedOnline(template, practice),
    });
  }

  const generalServices = items
    .filter((item) => item.classification === TreatmentClassification.General)
    .map((item) => item.name)
    .join(', ');

  const cosmeticServices = items
    .filter((item) => item.classification === TreatmentClassification.Cosmetic)
    .map((item) => item.name)
    .join(', ');

  return { items, generalServices, cosmeticServices };
}
